import {
  Children,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ElementType,
  type ReactNode,
  type JSX,
  type Key,
  type ContextType,
} from 'react'
import { jsx as originalJsx, jsxs as originalJsxs, Fragment } from 'react/jsx-runtime'
import { jsxDEV as originalJsxDEV, type JSXSource } from 'react/jsx-dev-runtime'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { ChatDisplayContext, ChatDisplayExcluded } from './ChatDisplayScope'
import ReactMarkdown from 'react-markdown'
import type { PresentedText } from './workOrderPresentation'
import { createChatDisplaySubscription } from './chatDisplaySubscription'
export { Fragment }
export type { JSX } from 'react'

type Props = Record<string, unknown> & { children?: ReactNode }
const EXCLUDED = new Set(['pre', 'code', 'script', 'style', 'svg', 'math'])
const URL = /https?:\/\/\S+|\b[\w.+-]+@[\w.-]+\.[a-z]{2,}\b/gi
const MARKUP =
  /```[\s\S]*?```|`[^`\n]+`|\[(?:doc|wo|norma|equip):[^\]]+\]|\]\([^)]*\)|\[|(?:^|\n)[ \t]*(?:#{1,6} |> |[-+*] |\d+\. )|[*_~]{1,3}|\||\n/g
const emptySnapshot = () => ''

function useDisplayRetention(
  presenter: ContextType<typeof ChatDisplayContext>,
  source: string[],
  locale: string | undefined,
  translation: { current: TFunction },
) {
  const observed = useMemo(
    () => createChatDisplaySubscription(presenter, source, locale, translation),
    [presenter, source, locale, translation],
  )
  useSyncExternalStore(observed.subscribe, observed.getSnapshot, emptySnapshot)
  return observed.retained
}

type DisplayResult = {
  values: PresentedText[]
  locale: string
  key: string
  presenter: ContextType<typeof ChatDisplayContext>
}
function matchingResult(
  result: DisplayResult | undefined,
  enabled: boolean,
  key: string,
  locale: string | undefined,
  presenter: ContextType<typeof ChatDisplayContext>,
) {
  return enabled &&
    result?.key === key &&
    result.locale === locale &&
    result.presenter === presenter
    ? result
    : undefined
}

function useDisplayStrings(values: string[], enabled = true) {
  const presenter = useContext(ChatDisplayContext)
  const { i18n, t } = useTranslation()
  const translation = useRef(t)
  translation.current = t
  const locale = i18n?.language ?? i18n?.resolvedLanguage
  const key = JSON.stringify(values)
  const source = useMemo(() => JSON.parse(key) as string[], [key])
  const retained = useDisplayRetention(presenter, source, locale, translation)
  const [result, setResult] = useState<DisplayResult>()
  useEffect(() => {
    if (!presenter || !locale || !enabled || !source.length) return
    let current = true
    void Promise.all(
      source.map((text) =>
        presenter.resolve(
          {
            kind: /^\s*(?:\{|\[\s*(?:\{|"))/.test(text) ? 'identity' : 'text',
            value: text,
            protectedValues: [...(text.match(URL) ?? []), ...(text.match(MARKUP) ?? [])],
          },
          locale,
          translation.current,
        ),
      ),
    ).then((texts) => {
      if (current) setResult({ values: texts, locale, key, presenter })
    })
    return () => {
      current = false
    }
  }, [presenter, source, key, locale, enabled])
  const current = matchingResult(result, enabled, key, locale, presenter)
  if (!enabled || !current) return values
  return source.map((value, index) => {
    const cached =
      presenter && locale
        ? presenter.present({ kind: 'text', value }, locale, translation.current)
        : undefined
    if (cached?.state === 'translated') return cached.text
    return retained.get(value) ?? current.values[index].text
  })
}
const ChatDisplayText = ({ text }: { text: string }) => useDisplayStrings([text])[0]
function ChatMarkdown(props: Props) {
  const presenter = useContext(ChatDisplayContext)
  const excluded = useContext(ChatDisplayExcluded)
  return !presenter || excluded
    ? originalJsx(ReactMarkdown, props)
    : originalJsx(ChatActiveMarkdown, props)
}
function ChatActiveMarkdown(props: Props) {
  const display = useDisplayStrings(typeof props.children === 'string' ? [props.children] : [])
  return originalJsx(ReactMarkdown, { ...props, children: display[0] ?? props.children })
}

/** React owns every text node; no DOM mutation, command projection or event interception. */
function ChatIntrinsic({ type, original }: { type: keyof JSX.IntrinsicElements; original: Props }) {
  const presenter = useContext(ChatDisplayContext)
  const excluded = useContext(ChatDisplayExcluded)
  if (!presenter || excluded) return originalJsx(type, original)
  return originalJsx(ChatActiveIntrinsic, { type, original })
}

function ChatActiveIntrinsic({
  type,
  original,
}: {
  type: keyof JSX.IntrinsicElements
  original: Props
}) {
  const skip =
    EXCLUDED.has(type) ||
    original['translate'] === 'no' ||
    Boolean(original['data-chat-no-translate'])
  const attributes = ['title', 'aria-label', 'placeholder'].filter(
    (key) => typeof original[key] === 'string',
  )
  const explicitOption =
    type === 'option' && original['value'] !== undefined && typeof original.children === 'string'
  const values = [
    ...attributes.map((key) => original[key] as string),
    ...(explicitOption ? [original.children as string] : []),
  ]
  const display = useDisplayStrings(values, !skip)
  if (skip || type === 'option' || type === 'input' || type === 'textarea') {
    const props = {
      ...original,
      ...Object.fromEntries(attributes.map((key, index) => [key, display[index]])),
      ...(explicitOption ? { children: display[attributes.length] } : {}),
    }
    return originalJsx(ChatDisplayExcluded.Provider, {
      value: true,
      children: originalJsx(type, props),
    })
  }
  const children = Children.map(original.children, (child) =>
    typeof child === 'string' && /[\p{L}]/u.test(child)
      ? originalJsx(ChatDisplayText, { text: child })
      : child,
  )
  return originalJsx(type, {
    ...original,
    children,
    ...Object.fromEntries(attributes.map((key, index) => [key, display[index]])),
  })
}

export function jsx(type: ElementType, props: Props, key?: Key) {
  return typeof type === 'string'
    ? originalJsx(intrinsicComponent(type), props, key)
    : originalJsx(type === ReactMarkdown ? ChatMarkdown : type, props, key)
}
const intrinsics = new Map<keyof JSX.IntrinsicElements, (props: Props) => ReactNode>()
function intrinsicComponent(type: keyof JSX.IntrinsicElements) {
  let component = intrinsics.get(type)
  if (!component) {
    component = (props: Props) => originalJsx(ChatIntrinsic, { type, original: props })
    intrinsics.set(type, component)
  }
  return component
}
export const jsxs = (type: ElementType, props: Props, key?: Key) =>
  originalJsxs(
    typeof type === 'string'
      ? intrinsicComponent(type)
      : type === ReactMarkdown
        ? ChatMarkdown
        : type,
    props,
    key,
  )
/** Preserve React's development source/self and static-child diagnostics. */
export const jsxDEV = (
  type: ElementType,
  props: Props,
  key: Key | undefined,
  isStatic: boolean,
  source?: JSXSource,
  self?: unknown,
) =>
  originalJsxDEV(
    typeof type === 'string'
      ? intrinsicComponent(type)
      : type === ReactMarkdown
        ? ChatMarkdown
        : type,
    props,
    key,
    isStatic,
    source,
    self,
  )
